// Edición de los atributos de un vídeo original: ejercicio, peso, repeticiones
// y %RM. En Storage esto renombra el objeto (los metadatos van en el nombre)
// conservando su timestamp, que es lo que enlaza sus correcciones.
import { useEffect, useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import ExercisePicker from "./ExercisePicker";

const numOrNull = (v) => (v === "" || v == null ? null : Number(v));

export default function EditVideoDetailsModal({
  isOpen,
  video,
  currentExercise,
  onClose,
  onSave,
  saving,
}) {
  const [exercise, setExercise] = useState(null);
  const [weight, setWeight] = useState("");
  const [reps, setReps] = useState("");
  const [rmPercent, setRmPercent] = useState("");
  const [error, setError] = useState("");

  // Al abrirlo para un vídeo, partir de sus valores actuales.
  useEffect(() => {
    if (!isOpen) return;
    setExercise(currentExercise || null);
    setWeight(video?.weight != null ? String(video.weight) : "");
    setReps(video?.reps != null ? String(video.reps) : "");
    setRmPercent(video?.rmPercent != null ? String(video.rmPercent) : "");
    setError("");
  }, [isOpen, video, currentExercise]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!exercise) {
      setError("Choose the exercise for this video.");
      return;
    }
    const w = numOrNull(weight);
    const r = numOrNull(reps);
    const rm = numOrNull(rmPercent);
    if (w != null && (!Number.isFinite(w) || w < 0)) {
      setError("Weight must be a positive number.");
      return;
    }
    if (r != null && (!Number.isInteger(r) || r < 0)) {
      setError("Reps must be a whole number.");
      return;
    }
    if (rm != null && (!Number.isFinite(rm) || rm < 0 || rm > 200)) {
      setError("%RM must be a number between 0 and 200.");
      return;
    }
    const ok = await onSave({ exerciseId: exercise.id, weight: w, reps: r, rmPercent: rm });
    if (ok) onClose();
    else setError("Could not save the changes. Please try again.");
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel className="relative w-full transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:max-w-lg sm:p-6">
            <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
              Edit video details
            </DialogTitle>
            {video && <p className="mt-1 truncate text-sm text-gray-500">{video.label}</p>}

            <div className="mt-4">
              <ExercisePicker value={exercise} onChange={setExercise} disabled={saving} />
            </div>

            <div className="mt-4 grid grid-cols-3 gap-3">
              <div>
                <label htmlFor="motion-weight" className="block text-sm font-medium text-gray-700">
                  Weight (kg)
                </label>
                <input
                  id="motion-weight"
                  type="number"
                  min="0"
                  step="0.5"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  disabled={saving}
                  placeholder="—"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 disabled:opacity-40 sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="motion-reps" className="block text-sm font-medium text-gray-700">
                  Reps
                </label>
                <input
                  id="motion-reps"
                  type="number"
                  min="0"
                  step="1"
                  value={reps}
                  onChange={(e) => setReps(e.target.value)}
                  disabled={saving}
                  placeholder="—"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 disabled:opacity-40 sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="motion-rm" className="block text-sm font-medium text-gray-700">
                  %RM
                </label>
                <input
                  id="motion-rm"
                  type="number"
                  min="0"
                  max="200"
                  step="0.5"
                  value={rmPercent}
                  onChange={(e) => setRmPercent(e.target.value)}
                  disabled={saving}
                  placeholder="—"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 disabled:opacity-40 sm:text-sm"
                />
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500">Leave a field empty to clear it.</p>

            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

            <div className="mt-5 grid grid-cols-1 gap-2 sm:mt-6 sm:grid-flow-row-dense sm:grid-cols-2">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:bg-gray-300 sm:col-start-2"
              >
                {saving ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                onClick={handleClose}
                disabled={saving}
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-40 sm:col-start-1 sm:mt-0"
              >
                Cancel
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
